var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { checkUserTypeOptional } from "healthex-common/checkusertype";
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
import { UserType } from "healthex-common/user.dtos";
import { computed, observable } from "mobx";
import { LoginStateScreen } from "healthex-common/device-hint.dtos";
import { getObservableT } from "../Infra/i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { getTranslatedStudyText } from "./TranslationService";
import { ClearAuthState, SocialLoginAuthState } from "../Infra/Persistence";
import { LoginSource } from "healthex-common/heap.dtos";
import { Disposer } from "../Infra/Disposer";
import { buildDeviceHintReauthOTPModel } from "./deviceHintReauthOTPModel";
import { isReauthLandingVisible } from "./deviceHintOTPReauth";
const t = getObservableT("patients");
export class PatientLandingViewModel {
    constructor(container) {
        this.disposer = new Disposer();
        this.isSandboxLogin = false;
        this.loginUIState = "login";
        this.loginStateRequested = false;
        this.loginUIManager = container.loginUIManager;
        this.routingUIManager = container.routingUIManager;
        this.patientConsentUIManager = container.patientConsentUIManager;
        this.authManager = container.authManager;
        this.persistence = container.persistence;
        this.clearAuthManager = container.clearAuthManager;
        this.socialLoginAuthManager = container.socialLoginAuthManager;
        this.deviceHintManager = container.deviceHintManager;
        this.deviceHintOTPManager = container.deviceHintOTPManager;
        this.isSandboxLogin = this.persistence.isSandboxLogin();
    }
    run() {
        this.loginStateRequested = true;
        void this.deviceHintManager.loadLoginState();
        this.disposer.reaction(() => this.deviceHintManager.loginState, (loginState) => {
            var _a;
            this.deviceHintOTPManager.pendingVerificationPhone = (_a = loginState === null || loginState === void 0 ? void 0 : loginState.maskedPhone) !== null && _a !== void 0 ? _a : null;
        }, { fireImmediately: true, name: "PatientLandingViewModel_deviceHintLoginState" });
        this.disposer.reaction(() => [this.clearAuthManager.state, this.routingUIManager.currentRoute], () => {
            if (this.clearAuthManager.hasCompletedAuth) {
                this.handleClearLoginResultIfNeeded();
            }
        }, { fireImmediately: true, name: "PatientLandingViewModel_clearAuthManagerState" });
        this.disposer.reaction(() => this.socialLoginAuthManager.state, () => {
            if (this.socialLoginAuthManager.hasCompletedAuth) {
                void this.handleSocialLoginResultIfNeeded();
            }
        }, { fireImmediately: true, name: "PatientLandingViewModel_socialLoginAuthManagerState" });
    }
    stop() {
        this.deviceHintOTPManager.resetVerificationFlow();
        this.disposer.dispose();
    }
    get phoneVerified() {
        return this.socialLoginAuthManager.pendingPhoneVerified;
    }
    get studyInfo() {
        if (!this.patientConsentUIManager.isRelevantRoute && !this.patientConsentUIManager.isInteractivePreview) {
            return null;
        }
        return this.patientConsentUIManager.studyInfo;
    }
    get organization() {
        if (this.patientConsentUIManager.isRelevantRoute || this.patientConsentUIManager.isInteractivePreview) {
            if (this.patientConsentUIManager.studyInfo === NOT_LOADED)
                return NOT_LOADED;
            if (this.patientConsentUIManager.studyInfo) {
                return this.patientConsentUIManager.studyInfo.addedByOrganization;
            }
        }
        if (this.loginUIManager.organization === NOT_LOADED)
            return NOT_LOADED;
        return checkUserTypeOptional(this.loginUIManager.organization, UserType.HEALTHCARE_ORGANIZATION, UserType.EXTERNAL_RESEARCHER);
    }
    get organizationIsLive() {
        if (this.organization === NOT_LOADED)
            return NOT_LOADED;
        if (!this.organization) {
            return false;
        }
        else {
            return this.organization.isLive;
        }
    }
    get pageTitle() {
        if (this.studyInfo) {
            return this.getCustomizedText("loginTitle");
        }
        else {
            return t("Welcome to HealthEx");
        }
    }
    get supportingText() {
        if (this.isLoginError) {
            return t("An error occurred. Please try again");
        }
        return t("Your HealthEx account keeps your records secure and lets you control who can access them.");
    }
    get clearButtonLabel() {
        if (this.isClearError) {
            return t("Try again with CLEAR");
        }
        return t("Continue with CLEAR");
    }
    get googleButtonLabel() {
        if (this.isGoogleError) {
            return t("Try again with Google");
        }
        return t("Continue with Google");
    }
    get microsoftButtonLabel() {
        if (this.isMicrosoftError) {
            return t("Try again with Microsoft");
        }
        return t("Continue with Microsoft");
    }
    get appleButtonLabel() {
        if (this.isAppleError) {
            return t("Try again with Apple");
        }
        return t("Continue with Apple");
    }
    get isAppleLoginButtonEnabled() {
        return this.patientConsentUIManager.isAppleLoginButtonEnabled;
    }
    get tryAgainButtonLabel() {
        if (this.isClearError) {
            return t("Try again with CLEAR");
        }
        return t("Continue with CLEAR");
    }
    get flipLongPressLoginButton() {
        return this.isSandboxLogin;
    }
    get isClearDefault() {
        if (this.loginUIManager.previousAuthenticationProvider === GFIDProvider.SMART_ON_FHIR && this.showProviderPortal) {
            return false;
        }
        return this.showClear;
    }
    get showClear() {
        var _a;
        if (!this.organization || this.organization === NOT_LOADED)
            return false;
        if (this.organization.type === UserType.EXTERNAL_RESEARCHER) {
            return true;
        }
        else {
            return (this.organization.patientAuthenticationProviders.includes(GFIDProvider.CLEAR) && !((_a = this.organization) === null || _a === void 0 ? void 0 : _a.forcePatientPortalEmbed));
        }
    }
    get showProviderPortal() {
        var _a;
        if (!this.organization || this.organization === NOT_LOADED)
            return false;
        if (this.organization.type === UserType.EXTERNAL_RESEARCHER) {
            return false;
        }
        else {
            return (this.organization.patientAuthenticationProviders.includes(GFIDProvider.SMART_ON_FHIR) &&
                !((_a = this.organization) === null || _a === void 0 ? void 0 : _a.forcePatientPortalEmbed));
        }
    }
    get showAnotherHealthcareProvider() {
        if (!this.organization || this.organization === NOT_LOADED)
            return false;
        return this.organization.type === UserType.HEALTHCARE_ORGANIZATION && this.studyInfo === null;
    }
    get isPreview() {
        if (this.patientConsentUIManager.isInteractivePreview) {
            return true;
        }
        return false;
    }
    get requestedGFIDProvider() {
        return this.socialLoginAuthManager.requestedGFIDProvider;
    }
    get responseGFIDProvider() {
        return this.socialLoginAuthManager.responseGFIDProvider;
    }
    get isClearLoggingIn() {
        return this.clearAuthManager.state === ClearAuthState.IN_PROGRESS;
    }
    get isGoogleLoggingIn() {
        return this.socialLoginAuthManager.state === SocialLoginAuthState.IN_PROGRESS && this.requestedGFIDProvider === GFIDProvider.GOOGLE;
    }
    get isMicrosoftLoggingIn() {
        return (this.socialLoginAuthManager.state === SocialLoginAuthState.IN_PROGRESS && this.requestedGFIDProvider === GFIDProvider.MICROSOFT);
    }
    get isAppleLoggingIn() {
        return this.socialLoginAuthManager.state === SocialLoginAuthState.IN_PROGRESS && this.requestedGFIDProvider === GFIDProvider.APPLE;
    }
    get isLoginInProgress() {
        return this.isClearLoggingIn || this.isGoogleLoggingIn || this.isMicrosoftLoggingIn || this.isAppleLoggingIn;
    }
    get isClearError() {
        return this.loginUIState === "verifyingWithClearError";
    }
    get isGoogleError() {
        return this.loginUIState === "verifyingWithSocialLoginError" && this.responseGFIDProvider === GFIDProvider.GOOGLE;
    }
    get isMicrosoftError() {
        return this.loginUIState === "verifyingWithSocialLoginError" && this.responseGFIDProvider === GFIDProvider.MICROSOFT;
    }
    get isAppleError() {
        return this.loginUIState === "verifyingWithSocialLoginError" && this.responseGFIDProvider === GFIDProvider.APPLE;
    }
    get isSocialLoginError() {
        return this.loginUIState === "verifyingWithSocialLoginError";
    }
    get isLoginError() {
        return this.isClearError || this.isSocialLoginError;
    }
    get isAccountLinkingError() {
        return this.socialLoginAuthManager.isAccountLinkingError;
    }
    get loginStateLoaded() {
        return this.deviceHintManager.loginStateLoaded;
    }
    get isLoadingLoginState() {
        return !this.loginStateRequested || this.deviceHintManager.loadingLoginState;
    }
    get loginScreen() {
        var _a, _b;
        return (_b = (_a = this.deviceHintManager.loginState) === null || _a === void 0 ? void 0 : _a.screen) !== null && _b !== void 0 ? _b : LoginStateScreen.STANDARD_LOGIN;
    }
    get reauthFirstName() {
        var _a;
        return (_a = this.deviceHintManager.loginState) === null || _a === void 0 ? void 0 : _a.firstName;
    }
    get showReauthLanding() {
        return isReauthLandingVisible({
            loginScreen: this.loginScreen,
            reauthFirstName: this.reauthFirstName,
            isLoggedIn: this.authManager.isLoggedIn,
        });
    }
    get isCompletingLogin() {
        return (this.authManager.isLoggedIn &&
            this.loginUIState === "login" &&
            !this.pendingSetupCompletion &&
            !this.pendingVerifyPhone &&
            !this.isAccountLinkingError);
    }
    get reauthProvider() {
        var _a;
        return (_a = this.deviceHintManager.loginState) === null || _a === void 0 ? void 0 : _a.provider;
    }
    get isForgettingDevice() {
        return this.deviceHintManager.forgettingDevice;
    }
    async forgetDevice() {
        this.deviceHintOTPManager.resetVerificationFlow();
        await this.deviceHintManager.forgetDevice();
    }
    get pendingSetupCompletion() {
        return this.loginUIManager.pendingSetupCompletion;
    }
    get pendingVerifyPhone() {
        return this.loginUIManager.pendingVerifyPhone;
    }
    startDeviceHintOTP() {
        var _a, _b;
        if (!this.checkIfLoginNeeded())
            return false;
        this.deviceHintOTPManager.pendingVerificationPhone = (_b = (_a = this.deviceHintManager.loginState) === null || _a === void 0 ? void 0 : _a.maskedPhone) !== null && _b !== void 0 ? _b : null;
        void this.deviceHintOTPManager.sendVerificationCode(true);
        return true;
    }
    resetDeviceHintOTP() {
        this.deviceHintOTPManager.resetVerificationFlow();
    }
    get reauthOTPModel() {
        return buildDeviceHintReauthOTPModel({
            deviceHintOTPManager: this.deviceHintOTPManager,
            isSandboxLogin: this.persistence.isSandboxLogin(),
            verifyWithClear: (sandboxMode = false) => {
                this.persistence.setSandboxLogin(sandboxMode);
                void this.startClearAuth(sandboxMode);
            },
        });
    }
    getCustomizedText(key) {
        if (this.studyInfo !== NOT_LOADED && this.studyInfo) {
            return getTranslatedStudyText(this.studyInfo, key, this.patientConsentUIManager.currentLanguage) || "";
        }
        return "";
    }
    checkIfLoginNeeded() {
        if (this.patientConsentUIManager.isInteractivePreview) {
            this.patientConsentUIManager.goToNextPage();
            return false;
        }
        if (this.isLoginInProgress)
            return false;
        return true;
    }
    async continueWithClear(sandboxMode) {
        if (!this.checkIfLoginNeeded())
            return;
        this.persistence.setSandboxLogin(sandboxMode);
        await this.startClearAuth(sandboxMode);
    }
    async continueWithGoogle() {
        if (!this.checkIfLoginNeeded())
            return;
        await this.startSocialLoginAuth(GFIDProvider.GOOGLE);
    }
    async continueWithMicrosoft() {
        if (!this.checkIfLoginNeeded())
            return;
        await this.startSocialLoginAuth(GFIDProvider.MICROSOFT);
    }
    async continueWithApple() {
        if (!this.checkIfLoginNeeded())
            return;
        await this.startSocialLoginAuth(GFIDProvider.APPLE);
    }
    async startClearAuth(sandboxMode) {
        var _a;
        if (!this.organization || this.organization === NOT_LOADED)
            return;
        if (this.patientConsentUIManager.patientCollectedEmail === null &&
            ((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) !== NamedRoutes.LOGIN_AS_PATIENT_EMAIL_COLLECTION.routeName) {
            this.routingUIManager.navigateTo(NamedRoutes.LOGIN_AS_PATIENT_EMAIL_COLLECTION);
            return;
        }
        await this.clearAuthManager.redirectToClearLoginPage(window.location.hash, LoginSource.USER_LOGIN, this.organization.id, false, sandboxMode, this.patientConsentUIManager.patientCollectedEmail);
    }
    async startSocialLoginAuth(gfidProvider) {
        if (!this.organization || this.organization === NOT_LOADED)
            return;
        await this.socialLoginAuthManager.redirectToSocialLoginPage(window.location.hash, gfidProvider, LoginSource.USER_LOGIN, this.organization.id);
    }
    async completeVerification() {
        var _a, _b, _c, _d;
        await this.authManager.waitForPendingRefresh();
        const pendingData = this.socialLoginAuthManager.pendingSocialLoginData;
        if (!pendingData)
            return;
        const result = await this.socialLoginAuthManager.completeVerificationFlow({
            firstName: (_a = pendingData.firstName) !== null && _a !== void 0 ? _a : "",
            lastName: (_b = pendingData.lastName) !== null && _b !== void 0 ? _b : "",
            dateOfBirth: (_c = pendingData.dateOfBirth) !== null && _c !== void 0 ? _c : "",
            phone: (_d = pendingData.phoneNumber) !== null && _d !== void 0 ? _d : "",
        });
        if (result.ok) {
            this.loginUIManager.completeSetupFlowAndEndLogin();
        }
    }
    handleClearLoginResultIfNeeded() {
        var _a, _b, _c;
        console.log(`handleClearLoginResultIfNeeded ${this.clearAuthManager.state}`);
        const clearAuthData = this.clearAuthManager.getClearAuthData();
        const isValidRoute = ((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) === NamedRoutes.LOGIN_AS_PATIENT.routeName ||
            ((_b = this.routingUIManager.currentRoute) === null || _b === void 0 ? void 0 : _b.routeName) === NamedRoutes.LOGIN_AS_PATIENT_EMAIL_COLLECTION.routeName;
        console.log(`isValidRoute ${isValidRoute} ${(_c = this.routingUIManager.currentRoute) === null || _c === void 0 ? void 0 : _c.routeName}`);
        console.log(`clearAuthData ${!!clearAuthData}`);
        if (!!clearAuthData && isValidRoute) {
            if (this.authManager.isLoggedIn) {
            }
            else {
                this.loginUIState = "verifyingWithClearError";
            }
            this.clearAuthManager.resetAuth();
        }
    }
    async handleSocialLoginResultIfNeeded() {
        var _a, _b;
        const socialLoginAuthData = this.socialLoginAuthManager.getSocialLoginAuthData();
        const isValidRoute = ((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) === NamedRoutes.LOGIN_AS_PATIENT.routeName ||
            ((_b = this.routingUIManager.currentRoute) === null || _b === void 0 ? void 0 : _b.routeName) === NamedRoutes.LOGIN_AS_PATIENT_EMAIL_COLLECTION.routeName;
        if (!!socialLoginAuthData && isValidRoute) {
            if (this.socialLoginAuthManager.hasPendingSocialLogin) {
                if (this.socialLoginAuthManager.shouldShowAccountSetupScreen === false) {
                    this.socialLoginAuthManager.setSocialLoginAuthData(null);
                    await this.completeVerification();
                    this.socialLoginAuthManager.resetAuth();
                    return;
                }
                this.loginUIManager.pendingSetupCompletion = true;
                this.socialLoginAuthManager.setSocialLoginAuthData(null);
                return;
            }
            if (this.authManager.isLoggedIn) {
            }
            else if (this.socialLoginAuthManager.isAccountLinkingError) {
                return;
            }
            else {
                this.loginUIState = "verifyingWithSocialLoginError";
            }
            this.socialLoginAuthManager.resetAuth();
        }
    }
    async continueWithOpenPortal() {
        if (!this.organization || this.organization === NOT_LOADED)
            return;
        this.routingUIManager.navigateTo(NamedRoutes.LOGIN_REDIRECT_TO_EMBED, { organizationId: this.organization.id });
    }
    loginAsOrganizationAdmin() {
        this.routingUIManager.navigateTo(NamedRoutes.LOGIN_AS_ORG_USER);
    }
    goBackToLoginMethodSelection() {
        this.clearAuthManager.resetAuth();
    }
    get isScreenshotMode() {
        return this.patientConsentUIManager.isScreenshotMode;
    }
}
__decorate([
    observable
], PatientLandingViewModel.prototype, "isSandboxLogin", void 0);
__decorate([
    observable
], PatientLandingViewModel.prototype, "loginUIState", void 0);
__decorate([
    observable
], PatientLandingViewModel.prototype, "loginStateRequested", void 0);
__decorate([
    computed
], PatientLandingViewModel.prototype, "phoneVerified", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "studyInfo", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "organization", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "organizationIsLive", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "pageTitle", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "supportingText", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "clearButtonLabel", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "googleButtonLabel", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "microsoftButtonLabel", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "appleButtonLabel", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isAppleLoginButtonEnabled", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "tryAgainButtonLabel", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "flipLongPressLoginButton", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isClearDefault", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "showClear", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "showProviderPortal", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "showAnotherHealthcareProvider", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isPreview", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "requestedGFIDProvider", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "responseGFIDProvider", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isClearLoggingIn", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isGoogleLoggingIn", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isMicrosoftLoggingIn", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isAppleLoggingIn", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isLoginInProgress", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isClearError", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isGoogleError", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isMicrosoftError", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isAppleError", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isSocialLoginError", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isLoginError", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isAccountLinkingError", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "loginStateLoaded", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isLoadingLoginState", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "loginScreen", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "reauthFirstName", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "showReauthLanding", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isCompletingLogin", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "reauthProvider", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isForgettingDevice", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "pendingSetupCompletion", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "pendingVerifyPhone", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "reauthOTPModel", null);
__decorate([
    computed
], PatientLandingViewModel.prototype, "isScreenshotMode", null);
